import { Injectable } from "@angular/core";
import { captureBlobImage } from "@auvious/media-tools";

/**
 * Containers we are willing to record, best first. `video/mp4` is preferred
 * because Reality Defender accepts mp4 directly and the backend can then
 * stream-copy rather than transcode. Everything else is remuxed server-side.
 */
const PREFERRED_CLIP_MIME_TYPES = [
  "video/mp4",
  'video/webm;codecs="h264"',
  "video/webm;codecs=vp8,opus",
  "video/webm"
];

/**
 * Audio-only containers, best first. Opus in webm is what Chrome and Firefox
 * produce; Safari records AAC in mp4. The backend converts whichever to wav.
 */
const PREFERRED_AUDIO_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4"
];

/**
 * Fixed recording bitrates, so a clip's size is predictable on every browser:
 * left alone, each browser picks its own default (and some pick high ones).
 * At 2.5 Mbps video + 128 kbps audio the 30 s maximum clip is about 10 MB,
 * far under the ai service's 60 MiB video part limit; an audio-only clip at
 * 128 kbps is about 0.5 MB, far under its 10 MiB audio limit.
 */
export const CLIP_BITRATES: Pick<
  MediaRecorderOptions,
  "videoBitsPerSecond" | "audioBitsPerSecond"
> = {
  videoBitsPerSecond: 2_500_000,
  audioBitsPerSecond: 128_000
};

export const AUDIO_CLIP_BITRATES: Pick<
  MediaRecorderOptions,
  "audioBitsPerSecond"
> = {
  audioBitsPerSecond: 128_000
};

export function isMediaRecorderAvailable(): boolean {
  return typeof MediaRecorder !== "undefined";
}

/**
 * Picks a container the browser will actually give us. We never assume — the
 * negotiated WebRTC codec is irrelevant here, MediaRecorder re-encodes.
 */
export function pickClipMimeType(): string | undefined {
  return pickSupported(PREFERRED_CLIP_MIME_TYPES);
}

export function pickAudioMimeType(): string | undefined {
  return pickSupported(PREFERRED_AUDIO_MIME_TYPES);
}

function pickSupported(candidates: string[]): string | undefined {
  if (!isMediaRecorderAvailable()) return undefined;

  return candidates.find((type) => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  });
}

/** What a recording that was cancelled (see `cancelAll`) rejects with. */
export class DeepfakeCaptureCancelledError extends Error {
  constructor() {
    super("The recording was cancelled");
    this.name = "DeepfakeCaptureCancelledError";
  }
}

/** Captures the still and records the bounded clips a deepfake check submits. */
@Injectable()
export class DeepfakeCaptureService {
  /** How to cancel each recording that is running. */
  private recordings = new Set<() => void>();

  /**
   * Stops every recording that is running and rejects it with
   * `DeepfakeCaptureCancelledError`: the call was left, nothing it recorded
   * is wanted any more.
   */
  public cancelAll(): void {
    [...this.recordings].forEach((cancel) => cancel());
  }

  /** A still of the participant's tile, as the browser encodes it. */
  public captureFrame(videoElement: HTMLVideoElement): Promise<Blob> {
    return captureBlobImage(videoElement);
  }

  /**
   * Records the participant's audio track alone. The remote camera stream
   * carries the microphone track (see ConferenceService.refreshRemoteAudio),
   * so this works from the video tile's element; an audio-only tile's
   * `<audio>` element works the same way.
   */
  public recordAudio(stream: MediaStream, durationMs: number): Promise<Blob> {
    const tracks = stream.getAudioTracks();

    if (!tracks.length) {
      return Promise.reject(
        new Error("The participant's audio is not available to record")
      );
    }

    return this.recordClip(
      new MediaStream(tracks),
      durationMs,
      pickAudioMimeType(),
      AUDIO_CLIP_BITRATES
    );
  }

  /**
   * Records a bounded clip. Isolated and defensive on purpose: this is the only
   * MediaRecorder use in the app.
   */
  public recordClip(
    stream: MediaStream,
    durationMs: number,
    mimeType = pickClipMimeType(),
    bitrates: MediaRecorderOptions = CLIP_BITRATES
  ): Promise<Blob> {
    if (!mimeType) {
      return Promise.reject(new Error("This browser cannot record a clip"));
    }

    return new Promise<Blob>((resolve, reject) => {
      let recorder: MediaRecorder;

      try {
        recorder = new MediaRecorder(stream, { mimeType, ...bitrates });
      } catch (ex) {
        reject(ex);
        return;
      }

      const chunks: Blob[] = [];
      let stopTimer: ReturnType<typeof setTimeout>;

      const cleanup = () => {
        clearTimeout(stopTimer);
        this.recordings.delete(cancel);
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
      };

      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunks.push(event.data);
      };

      recorder.onerror = (event) => {
        cleanup();
        reject((event as unknown as { error?: Error })?.error || event);
      };

      recorder.onstop = () => {
        cleanup();

        // `recorder.mimeType` is what the browser actually produced, which is
        // not necessarily what we asked for. The backend needs the real value.
        const type = recorder.mimeType || mimeType;
        const blob = new Blob(chunks, { type });

        if (!blob.size) {
          reject(new Error("The recorded clip was empty"));
          return;
        }

        resolve(blob);
      };

      // handlers off first: stopping fires `onstop`, which would resolve
      const cancel = () => {
        cleanup();

        try {
          if (recorder.state !== "inactive") recorder.stop();
        } catch {
          // stopping is all that is wanted, and it is stopped either way
        }

        reject(new DeepfakeCaptureCancelledError());
      };

      try {
        recorder.start();
      } catch (ex) {
        cleanup();
        reject(ex);
        return;
      }

      this.recordings.add(cancel);

      stopTimer = setTimeout(() => {
        try {
          if (recorder.state !== "inactive") recorder.stop();
        } catch (ex) {
          cleanup();
          reject(ex);
        }
      }, durationMs);
    });
  }
}
